import * as THREE from 'three';

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function makePerm(seed) {
  const src = new Uint8Array(256);
  for (let i = 0; i < 256; i += 1) src[i] = i;
  let s = seed >>> 0;
  for (let i = 255; i > 0; i -= 1) {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    const j = s % (i + 1);
    const tmp = src[i];
    src[i] = src[j];
    src[j] = tmp;
  }
  const p = new Uint8Array(512);
  for (let i = 0; i < 512; i += 1) p[i] = src[i & 255];
  return p;
}

const GRAD2 = [[1, 1], [-1, 1], [1, -1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]];

function perlin2(x, y, perm) {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const xf = x - x0;
  const yf = y - y0;
  const X = x0 & 255;
  const Y = y0 & 255;
  const g00 = GRAD2[perm[X + perm[Y]] & 7];
  const g10 = GRAD2[perm[X + 1 + perm[Y]] & 7];
  const g01 = GRAD2[perm[X + perm[Y + 1]] & 7];
  const g11 = GRAD2[perm[X + 1 + perm[Y + 1]] & 7];
  const n00 = g00[0] * xf + g00[1] * yf;
  const n10 = g10[0] * (xf - 1) + g10[1] * yf;
  const n01 = g01[0] * xf + g01[1] * (yf - 1);
  const n11 = g11[0] * (xf - 1) + g11[1] * (yf - 1);
  const u = fade(xf);
  const v = fade(yf);
  return n00 + u * (n10 - n00) + v * ((n01 + u * (n11 - n01)) - (n00 + u * (n10 - n00)));
}

function fbm2(x, y, perm, octaves) {
  let sum = 0;
  let amp = 0.5;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i += 1) {
    sum += perlin2(x * freq, y * freq, perm) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2.03;
  }
  return sum / norm;
}

/** Float Perlin — the first clouds, without running fbm in the fragment. */
function bakeSmokeNoise() {
  const size = 1024;
  const data = new Float32Array(size * size * 4);
  const permA = makePerm(0x9e3779b9);
  const permB = makePerm(0x85ebca6b);
  const freq = 0.028 * (512 / size);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const fx = x * freq;
      const fy = y * freq;
      const a = fbm2(fx, fy, permA, 5) * 0.5 + 0.5;
      const b = fbm2(fx + 19.1, fy + 8.4, permB, 4) * 0.5 + 0.5;
      const i = (y * size + x) * 4;
      data[i] = a;
      data[i + 1] = b;
      data[i + 2] = 1 - Math.abs(b * 2 - 1);
      data[i + 3] = 1;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.FloatType);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 8;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

let smokeNoise = null;
let smokeNoiseRev = 0;
const SMOKE_NOISE_REV = 2;

export function getSmokeNoise() {
  if (!smokeNoise || smokeNoiseRev !== SMOKE_NOISE_REV) {
    smokeNoise?.dispose?.();
    smokeNoise = bakeSmokeNoise();
    smokeNoiseRev = SMOKE_NOISE_REV;
  }
  return smokeNoise;
}
